/**
 * Recuperar só parte do pedido cancelado (Douglas 23/10/2026): o cliente não quer
 * mais 3 das 16 linhas. Na revisão do cancelado o consultor marca essas linhas como
 * "Fica cancelada"; o resto volta para PEDIDO RECUPERADO. Nada é apagado: a linha que
 * fica continua na proposta e o item continua no cancelado, com o próprio registro.
 *
 * A marca é por ITEM do pedido (id), não por linha da proposta: um item agrupado
 * (renovação em grade, bloco) responde por várias linhas, e todas ficam juntas.
 * O serviço que acompanha uma linha (Claro Monitor, Passaport) segue a linha dona.
 *
 * Mesma régua na tela (MontadorComposicaoPlano, cartão Recuperar Pedido) e na
 * recuperação (useRegenerarItensPedido), para o que a tela promete ser o que acontece.
 */

import { linhaPertenceAItem, type ItemPedidoMatch, type LinhaPropostaMatch } from './linhas-protegidas';

export interface LinhaRevisaoMatch extends LinhaPropostaMatch {
  tipo?: string | null;
  linhaVinculadaId?: string | null;
  linhaVinculada?: string | null;
}

const soDigitos = (s: unknown): string => String(s ?? '').replace(/\D/g, '');

function mesmoNumero(a: unknown, b: unknown): boolean {
  const da = soDigitos(a);
  const db = soDigitos(b);
  if (da.length < 8 || db.length < 8) return false;
  return da === db || da.endsWith(db) || db.endsWith(da);
}

/** O item do pedido que esta linha representa, por linha_id primeiro e telefone depois. */
export function itemDaLinha<T extends ItemPedidoMatch>(linha: LinhaRevisaoMatch, itens: T[]): T | null {
  const exato = linha.linha_id ? (itens || []).find((i) => i.linha_id === linha.linha_id) : undefined;
  if (exato) return exato;
  if (linha.tipo === 'SERVICO') return null;
  return (itens || []).find((i) => linhaPertenceAItem(linha, i)) ?? null;
}

/** Só item cancelado pode ficar cancelado ou voltar; o resto não se mexe na revisão. */
export function itemCanceladoDaLinha<T extends ItemPedidoMatch>(linha: LinhaRevisaoMatch, itens: T[]): T | null {
  const item = itemDaLinha(linha, itens);
  return item && item.quadro_atual === 'PEDIDO_CANCELADO' ? item : null;
}

export function donaDoServico<L extends LinhaRevisaoMatch>(servico: LinhaRevisaoMatch, linhas: L[]): L | null {
  if (servico.tipo !== 'SERVICO') return null;
  return (linhas || []).find((l) => l.tipo !== 'SERVICO' && (
    servico.linhaVinculadaId && l.linha_id
      ? servico.linhaVinculadaId === l.linha_id
      : mesmoNumero(servico.linhaVinculada, l.numero)
  )) ?? null;
}

export function linhaFicaCancelada<L extends LinhaRevisaoMatch>(
  linha: L,
  linhas: L[],
  itens: ItemPedidoMatch[],
  ficam: ReadonlySet<string>,
): boolean {
  if (ficam.size === 0) return false;
  const item = itemCanceladoDaLinha(linha, itens);
  if (item?.id && ficam.has(item.id)) return true;
  const dona = donaDoServico(linha, linhas);
  return !!dona && dona !== linha && linhaFicaCancelada(dona, linhas, itens, ficam);
}

export interface SeparacaoRecuperacao<L> {
  volta: L[];
  fica: L[];
}

/**
 * Separa as linhas da proposta entre o que volta para o pedido e o que fica no
 * cancelado. Linha de item que não está cancelado (em andamento, finalizado ou de
 * outro pedido do cliente) não entra em nenhum dos dois: a revisão não mexe nela.
 * Linha sem item nenhum é linha nova: entra no pedido recuperado, então volta.
 */
export function separarRecuperacao<L extends LinhaRevisaoMatch>(
  linhas: L[],
  itensDoPedido: ItemPedidoMatch[],
  ficam: ReadonlySet<string>,
  itensOutrosPedidos: ItemPedidoMatch[] = [],
): SeparacaoRecuperacao<L> {
  const volta: L[] = [];
  const fica: L[] = [];
  for (const linha of linhas || []) {
    if ((itensOutrosPedidos || []).some((i) => linhaPertenceAItem(linha, i))) continue;
    const item = itemDaLinha(linha, itensDoPedido);
    if (item && item.quadro_atual !== 'PEDIDO_CANCELADO') continue;
    const dona = donaDoServico(linha, linhas);
    if (!item && dona) {
      const itemDona = itemDaLinha(dona, itensDoPedido);
      if (itemDona && itemDona.quadro_atual !== 'PEDIDO_CANCELADO') continue;
    }
    (linhaFicaCancelada(linha, linhas, itensDoPedido, ficam) ? fica : volta).push(linha);
  }
  return { volta, fica };
}
